import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, CheckCircle2, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useLang } from "@/context/LanguageContext";
import { applyAiot, errText, loginUrl, PORTAL } from "@/lib/aiot";

const F = ({ id, label, children }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-sm font-semibold text-[#0B1F3A]">{label}</Label>
    {children}
  </div>
);

const Done = ({ res, email }) => {
  const { L } = useLang();
  return (
    <div className="text-center py-6" data-testid="aiot-apply-success">
      <CheckCircle2 className="w-14 h-14 mx-auto text-[#059669]" />
      <h3 className="mt-4 text-2xl font-extrabold text-[#0B1F3A]">{res.updated ? L("Application updated", "आवेदन अपडेट हो गया") : L("Application received", "आवेदन मिल गया")}</h3>
      <p className="mt-2 text-sm text-slate-600 max-w-sm mx-auto">{L("Our team will call you with the fees and next batch details. Status:", "हमारी टीम फ़ीस और अगले बैच की जानकारी के लिए आपको कॉल करेगी। स्टेटस:")} <b className="text-[#0F766E]" data-testid="aiot-apply-status">{res.status}</b></p>
      <div className="mt-6 rounded-2xl bg-[#F0FDFA] border border-[#99F6E4] p-4 text-left text-sm text-slate-700">
        {L("Next step: create or sign in to your RoboSpark student account with the same email", "अगला कदम: इसी ईमेल से अपना RoboSpark स्टूडेंट अकाउंट बनाइए या साइन इन कीजिए")} (<b>{email}</b>). {L("Classes unlock there once the admin approves you.", "एडमिन की मंज़ूरी के बाद क्लास वहीं खुल जाएँगी।")}
      </div>
      <div className="mt-5 flex flex-wrap justify-center gap-3">
        <Link to={loginUrl(PORTAL)} className="rounded-full bg-[#0B1F3A] text-white px-5 h-11 inline-flex items-center font-bold hover:bg-[#0F766E]" data-testid="aiot-apply-login">{L("Student sign in", "स्टूडेंट साइन इन")}</Link>
        <Link to={`${PORTAL}?lecture=d1`} className="rounded-full border border-slate-300 px-5 h-11 inline-flex items-center font-bold text-[#0B1F3A]" data-testid="aiot-apply-demo">{L("Start free demos", "फ़्री डेमो शुरू करें")}</Link>
      </div>
    </div>
  );
};

export const ApplyForm = () => {
  const { L } = useLang();
  const [f, setF] = useState({ name: "", phone: "", email: "", city: "", background: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [res, setRes] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const phone = f.phone.replace(/\D/g, "").slice(-10);
    if (!/^[6-9]\d{9}$/.test(phone)) { setErr(L("Please enter a valid 10-digit Indian mobile number.", "कृपया सही 10 अंकों का मोबाइल नंबर डालें।")); return; }
    setBusy(true); setErr("");
    try { setRes(await applyAiot({ ...f, phone })); } catch (x) { setErr(errText(x, L("Could not send your application. Please try again.", "आवेदन नहीं भेजा जा सका। फिर से कोशिश करें।"))); } finally { setBusy(false); }
  };

  if (res) return <Done res={res} email={f.email} />;
  const cls = "h-11 bg-white focus-visible:ring-[#0F766E]";
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="aiot-apply-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <F id="aiot-name" label={L("Full name", "पूरा नाम")}><Input id="aiot-name" required minLength={2} value={f.name} onChange={set("name")} className={cls} data-testid="aiot-apply-name" /></F>
        <F id="aiot-phone" label={L("Mobile number", "मोबाइल नंबर")}><Input id="aiot-phone" required inputMode="tel" placeholder="98XXXXXXXX" value={f.phone} onChange={set("phone")} className={cls} data-testid="aiot-apply-phone" /></F>
        <F id="aiot-email" label={L("Email", "ईमेल")}><Input id="aiot-email" type="email" required value={f.email} onChange={set("email")} className={cls} data-testid="aiot-apply-email" /></F>
        <F id="aiot-city" label={L("City", "शहर")}><Input id="aiot-city" required minLength={2} value={f.city} onChange={set("city")} className={cls} data-testid="aiot-apply-city" /></F>
      </div>
      <F id="aiot-bg" label={L("Current role or education", "वर्तमान भूमिका या पढ़ाई")}>
        <Input id="aiot-bg" required minLength={2} list="aiot-bg-list" placeholder={L("e.g. B.Tech ECE, 3rd year", "जैसे B.Tech ECE, तीसरा साल")} value={f.background} onChange={set("background")} className={cls} data-testid="aiot-apply-background" />
        <datalist id="aiot-bg-list">{["Class 12 student", "Diploma student", "B.Tech / B.E. student", "B.Sc / BCA student", "Graduate", "Working professional", "Electrician / technician"].map((o) => <option key={o} value={o} />)}</datalist>
      </F>
      <F id="aiot-msg" label={L("Anything you want us to know (optional)", "कुछ और बताना चाहें (वैकल्पिक)")}><Textarea id="aiot-msg" rows={3} maxLength={600} value={f.message} onChange={set("message")} className="bg-white focus-visible:ring-[#0F766E]" data-testid="aiot-apply-message" /></F>
      {err && <p className="text-sm font-semibold text-[#DC2626]" data-testid="aiot-apply-error">{err}</p>}
      <button type="submit" disabled={busy} className="w-full h-12 rounded-full bg-[#0F766E] text-white font-bold inline-flex items-center justify-center gap-2 transition-[background-color,transform] hover:bg-[#0B1F3A] active:scale-[0.98] disabled:opacity-60" data-testid="aiot-apply-submit">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}{L("Send application", "आवेदन भेजें")}
      </button>
      <p className="text-xs text-slate-500 text-center">{L("Fees and next batch shared after you apply. No payment now.", "फ़ीस और अगला बैच आवेदन के बाद बताया जाएगा। अभी कोई भुगतान नहीं।")}</p>
    </form>
  );
};
